import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TNil } from '@app/app.interface';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import {
  IImageZoomDialogData,
  ImageZoomDialogComponent,
} from '@shared/components/image-zoom-dialog/image-zoom-dialog.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { IAdminUserFeedbackDetailsDialogData } from './admin-user-feedback-details.interface';

@Component({
  imports: [CommonModule, SideDialogComponent, TranslateModule, NgxSkeletonLoaderModule, IconComponent],
  selector: 'app-admin-user-feedback-details',
  templateUrl: './admin-user-feedback-details.component.html',
})
export class AdminUserFeedbackDetailsComponent {
  public readonly imageUrl = signal<string | TNil>(undefined);
  public readonly creatorName = signal<string | null>(null);

  public readonly data = inject<IAdminUserFeedbackDetailsDialogData>(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef<AdminUserFeedbackDetailsComponent>);
  private readonly imageUtilitiesService = inject(ImageUtilitiesService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.creatorName.set(this.resolveCreatorName(this.data));

    if (this.data.feedback.imageId) {
      this.imageUtilitiesService
        .getImage(this.data.feedback.imageId)
        .pipe(takeUntilDestroyed())
        .subscribe({
          next: (url: string) => {
            const currentUrl = this.imageUrl();

            if (currentUrl) {
              URL.revokeObjectURL(currentUrl);
            }

            this.imageUrl.set(url);
          },

          error: () => this.imageUrl.set(null),
        });
    }

    this.destroyRef.onDestroy(() => {
      const currentUrl = this.imageUrl();

      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    });
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onImageClick(): void {
    const imageUrl = this.imageUrl();

    if (!imageUrl) {
      return;
    }

    const data: IImageZoomDialogData = {
      feedback: this.data.feedback,
      imageUrl,
    };

    this.dialog.open(ImageZoomDialogComponent, {
      data,
      height: '90vh',
      maxHeight: '90vh',
      maxWidth: '90vw',
      width: '90vw',
    });
  }

  private resolveCreatorName(data: IAdminUserFeedbackDetailsDialogData): string | null {
    const { creator, feedback } = data;

    if (creator?.name && feedback.createdBy && feedback.createdBy !== creator.name) {
      return `${creator.name} (${feedback.createdBy})`;
    }

    if (creator?.name) {
      return creator.name;
    }

    return feedback.createdBy ?? null;
  }
}
